import { useState } from 'react'
import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { LuCalendar as Calendar, LuCalendarPlus } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import { DataTable, DataList, PageHeader, ConfirmDialog } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { programacionesService } from '../../api/services'
import { usePagedRequest } from '../../hooks'
import type { ScheduleListItemResponse } from '../../api/models/response/programacion'
import { SCHEDULE_STATUSES } from '../../data/enums'
import { useT } from '../../i18n'
import { exportToExcel, type ExcelColumn } from '../../utils/exportExcel'
import { useHasPermission } from '../../auth/PermissionContext'

const STATUS_COLORS: Record<number, { bg: string; color: string }> = {
  0: { bg: 'rgba(212, 154, 42, 0.15)', color: '#8a5f0b' },   // Borrador - amber
  1: { bg: 'rgba(61, 111, 168, 0.12)', color: '#244775' },    // Programado - blue
  2: { bg: 'rgba(42, 138, 135, 0.12)', color: '#1d6664' },     // En Proceso - green
  3: { bg: 'rgba(181, 170, 157, 0.15)', color: '#5f574f' },   // Completado - gray
}

const excelColumns: ExcelColumn<ScheduleListItemResponse>[] = [
  { header: 'Código', accessor: (s) => s.code ?? 'Borrador' },
  { header: 'Nombre', accessor: (s) => s.name ?? '-' },
  { header: 'Fecha Inicio', accessor: (s) => s.startDate },
  { header: 'Fecha Fin', accessor: (s) => s.endDate },
  { header: 'Geólogos', accessor: (s) => s.geologistCount },
  { header: 'Proveedores', accessor: (s) => s.supplierCount },
  { header: 'DMs', accessor: (s) => s.miningRightCount },
  { header: 'Estado', accessor: (s) => SCHEDULE_STATUSES.find((st) => st.value === s.scheduleStatus)?.label ?? 'Desconocido' },
]

const formatShortDate = (dateStr: string) => {
  if (!dateStr) return ''
  const parts = dateStr.split('-')
  if (parts.length === 3) return `${parts[2]}/${parts[1]}`
  return dateStr
}

export default function ProgramacionesPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const { t } = useT()
  const canCreate = useHasPermission('fieldVisits.create')
  const canDelete = useHasPermission('fieldVisits.update')
  const [search, setSearch] = useState('')

  const { items: schedules, totalCount, isFetching: loading, error, refresh, page, setPage, totalPages, pageSize, setPageSize } = usePagedRequest(
    programacionesService.getAll,
    { params: { search: search.trim() || undefined } },
  )

  const [deleteTarget, setDeleteTarget] = useState<ScheduleListItemResponse | null>(null)

  const handleDelete = (schedule: ScheduleListItemResponse) => {
    setDeleteTarget(schedule)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    await programacionesService.delete(deleteTarget.id)
    setDeleteTarget(null)
    refresh()
  }

  return (
    <VStack gap={5} alignItems="stretch">
      <PageHeader
        title={t('programaciones.title')}
        subtitle={t('programaciones.subtitle')}
        buttonLabel={canCreate ? t('programaciones.newButton') : undefined}
        onButtonClick={canCreate ? () => navigate('/app/programaciones/nuevo') : undefined}
        buttonIcon={canCreate ? <LuCalendarPlus size={18} /> : undefined}
        colors={colors}
      />

      <DataList<ScheduleListItemResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('programaciones.searchPlaceholder')}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        entityName="programaciones"
        onExport={() => exportToExcel(schedules, excelColumns, 'programaciones')}
        brandColor={brand.green}
        headers={[
          t('programaciones.headers.codigo'),
          'Nombre',
          t('programaciones.headers.fechas'),
          t('programaciones.headers.geologos'),
          t('programaciones.headers.proveedores'),
          t('programaciones.headers.dms'),
          t('programaciones.headers.estado'),
        ]}
        data={schedules}
        loading={loading}
        error={error}
        emptyMessage={t('programaciones.emptyMessage')}
        loadingMessage={t('programaciones.loadingMessage')}
        colors={colors}
        keyExtractor={(s) => s.id}
        renderRow={(schedule) => (
          <>
            {/* Codigo */}
            <DataTable.Cell>
              <Text fontFamily="'JetBrains Mono', monospace" fontSize="sm" fontWeight="600" color={colors.text}>
                {schedule.code ?? t('programaciones.status.draft')}
              </Text>
            </DataTable.Cell>

            {/* Nombre */}
            <DataTable.Cell>
              <Box>
                <Text fontSize="sm" fontWeight="500" color={colors.text}>
                  {schedule.name ?? '-'}
                </Text>
              </Box>
            </DataTable.Cell>

            {/* Fechas - compact with calendar icon */}
            <DataTable.Cell>
              <Flex alignItems="center" gap={1.5}>
                <Box color={colors.textMuted} flexShrink={0}>
                  <Calendar size={14} />
                </Box>
                <Text fontSize="sm" color={colors.text}>
                  {formatShortDate(schedule.startDate)} - {formatShortDate(schedule.endDate)}
                </Text>
              </Flex>
            </DataTable.Cell>

            {/* Geologos */}
            <DataTable.Cell>
              <Box
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                width="30px"
                height="30px"
                borderRadius="full"
                bg="rgba(21, 128, 61, 0.1)"
                fontSize="sm"
                fontWeight="600"
                color="#1d6664"
              >
                {schedule.geologistCount}
              </Box>
            </DataTable.Cell>

            {/* Proveedores */}
            <DataTable.Cell>
              <Box
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                width="30px"
                height="30px"
                borderRadius="full"
                bg="rgba(37, 99, 235, 0.1)"
                fontSize="sm"
                fontWeight="600"
                color="#2b5285"
              >
                {schedule.supplierCount}
              </Box>
            </DataTable.Cell>

            {/* DMs */}
            <DataTable.Cell>
              <Box
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                width="30px"
                height="30px"
                borderRadius="full"
                bg="rgba(180, 83, 9, 0.1)"
                fontSize="sm"
                fontWeight="600"
                color="#8a5f0b"
              >
                {schedule.miningRightCount}
              </Box>
            </DataTable.Cell>

            {/* Estado */}
            <DataTable.Cell>
              <Box
                display="inline-block"
                px={2.5}
                py={1}
                borderRadius="full"
                fontSize="10px"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="0.06em"
                bg={STATUS_COLORS[schedule.scheduleStatus]?.bg ?? 'rgba(181, 170, 157,0.15)'}
                color={STATUS_COLORS[schedule.scheduleStatus]?.color ?? '#5f574f'}
              >
                {SCHEDULE_STATUSES.find((s) => s.value === schedule.scheduleStatus)?.label ?? t('programaciones.status.unknown')}
              </Box>
            </DataTable.Cell>
          </>
        )}
        actions={(schedule) => {
          const acts: { label: string; onClick: () => void; color?: string }[] = []
          const isEditable = schedule.scheduleStatus === 0 || schedule.scheduleStatus === 1
          if (schedule.scheduleStatus === 0) {
            if (canCreate) acts.push({ label: t('programaciones.actions.continueReg'), onClick: () => navigate(`/app/programaciones/${schedule.id}`) })
          } else {
            acts.push({ label: t('programaciones.actions.viewDetail'), onClick: () => navigate(`/app/programaciones/${schedule.id}/detalle`) })
            if (canCreate && schedule.scheduleStatus === 1) acts.push({ label: t('programaciones.actions.edit'), onClick: () => navigate(`/app/programaciones/${schedule.id}`) })
          }
          if (canDelete && isEditable) acts.push({ label: t('programaciones.actions.delete'), onClick: () => handleDelete(schedule), color: '#b23a26' })
          return acts
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Eliminar Programacion"
        confirmLabel="Eliminar"
        variant="danger"
      >
        ¿Esta seguro que desea eliminar la programacion <strong>{deleteTarget?.code}</strong>? Esta accion no se puede deshacer.
      </ConfirmDialog>
    </VStack>
  )
}
